import { Component } from '@angular/core';
import { PatientSessionModel, TargetType } from '../../core/api/models';
import { AlertService } from '../../shared/services/alert.service';
import { SupervisorHelperService } from '../supervisor.helper.service';
import { ActivatedRoute, Router } from '@angular/router';
import { StaffApiService, SupervisorApiService } from '../../core/api/services';
import { SelectItem } from 'primeng/api';
import { Utils } from '../../core/utils';
import { SupervisorBaseComponent } from '../supervisor-base.component';
import { AuthService } from '../../auth/auth.service';
import { PatientDataFacadeService } from '../../core/services/patient-data-facade.service';

@Component({
  selector: 'app-move-session-data',
  templateUrl: './move-session-data.component.html',
  styleUrl: './move-session-data.component.css',
  standalone: false,
})
export class MoveSessionDataComponent extends SupervisorBaseComponent {
  patientID: string = '';
  patientIDLoaded: boolean = false;
  loading: boolean = false;

  sessionDataToMove: {
    SessionIDSource?: number;
    SessionIDDestination?: number;
  } = {
    SessionIDSource: 0,
    SessionIDDestination: 0,
  };
  sourceSessions: SelectItem[] = [];
  destinationSessions: SelectItem[] = [];
  allSessions: PatientSessionModel[] = [];

  constructor(
    protected override supervisorApiService: SupervisorApiService,
    protected override alertService: AlertService,
    protected override supervisorHelperService: SupervisorHelperService,
    protected override authService: AuthService,
    protected override staffApiService: StaffApiService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    patientDataFacadeService: PatientDataFacadeService
  )  {
    super(
      supervisorApiService,
      alertService,
      supervisorHelperService,
      authService,
      staffApiService,
      patientDataFacadeService
    );
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params) => {
      this.patientID = params['patientID'];
      this.patientIDLoaded = true;
      if (!this.patientID) {
        this.router.navigate(['/select-patient']);
      }
      
      this.loading = true;
      const getSessionsObservable = this.staffApiService.GetPatientSessions({
        patientID: parseInt(this.patientID),
      });
      getSessionsObservable.subscribe({
        next: (sessions: PatientSessionModel[]) => {
          // Sort sessions by StartedDate descending
          sessions.sort((a, b) => new Date(b.StartedDate ?? '').getTime() - new Date(a.StartedDate ?? '').getTime());
          this.initLoadSessions(sessions);
          this.loadBaseData(Number(this.patientID), () => {
            this.loading = false;
          });
        },
        error: (error: any) => {
          this.loading = false;
        }
      });
    });
  }

  initLoadSessions(sessions: PatientSessionModel[]) {
    console.log(sessions);
    this.allSessions = sessions;
    this.sourceSessions = this.allSessions.filter((s) => true).map((s) => ({ label: this.getSessionName(s.ID ?? 0), value: s.ID ?? 0 }));
    this.destinationSessions = this.allSessions.filter((s) => true).map((s) => ({ label: this.getSessionName(s.ID ?? 0), value: s.ID ?? 0 }));
  }

  onSourceSessionChange(session: { SessionIDSource?: number; SessionIDDestination?: number; moveDataSessions: PatientSessionModel[]; }) {
    console.log(session);
    session.moveDataSessions = this.allSessions.filter((s) => s.ID !== session.SessionIDSource);
  }

  moveSessionData() {
    console.log(this.sessionDataToMove);

    if (this.sessionDataToMove.SessionIDSource === 0 || this.sessionDataToMove.SessionIDDestination === 0) {
      this.alertService.error('Please select a source and destination session');
      return;
    }

    this.alertService.confirm('Are you sure you want to move the selected data between the sessions? This operation cannot be undone.', 'Confirm').subscribe({
      next: (result: boolean) => {
        if (result) {
          this.loading = true;
          this.supervisorApiService.MoveSessionData({
            body: {
                SessionIDSource: this.sessionDataToMove.SessionIDSource,
                SessionIDDestination: this.sessionDataToMove.SessionIDDestination,
            }
          }).subscribe({
            next: (response) => {
              this.loading = false;
              this.alertService.success('Data moved successfully');
            },
            error: (error) => {
              this.loading = false;
              this.alertService.error('Error moving data');
            }
          });
        }
      }
    });
  }

  getSessionName(sessionID: number) {
    if (!sessionID) {
      return 'Select Session';
    }
    const session = this.allSessions.find((s) => s.ID === sessionID);
    if (!session) {
      return 'Select Session';
    }
    const dateToDisplay = new Date(session.StartedDate ?? '').toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const serviceID = session.ServiceID ?? 'No Service';
    const staffName = (session.StaffName == null || session.StaffName == '') ? 'No Staff Name' : session.StaffName;
    return 'Service #' + serviceID + ' (' + dateToDisplay + ') ' + 'by ' + staffName;
  }

  getTargetDataCount(sessionID: number) {
    const session = this.allSessions.find((s) => s.ID === sessionID);
    if (!session) {
      return 0;
    }
    return session.TargetData?.length ?? 0;
  }

  getBehaviorDataCount(sessionID: number) {
    const session = this.allSessions.find((s) => s.ID === sessionID);
    if (!session) {
      return 0;
    }
    return session.BehaviorData?.length ?? 0;
  }

  getTargetData(targetID: number) {
    const target = this.supervisorHelperService.targets().find((s) => s.ID === targetID);
    if (!target) {
      return '';
    }
    return target.TargetName + ' (' + Utils.patientTargetTypeLabel(target.TargetType!) + ')';
  }

  getBehaviorData(behaviorID: number) {
    const behavior = this.supervisorHelperService.behaviors().find((s) => s.ID === behaviorID);
    if (!behavior) {
      return '';
    }
    return behavior.PatientBehaviorName + ' (' + Utils.patientBehaviorTypeLabel(behavior.BehaviorType!) + ')';
  }

  getTargetDataFromTargetID(targetID: number) {
    const target = this.supervisorHelperService.targets().find((s) => s.ID === targetID);
    if (!target) {
      return '';
    }
    return target.TargetName + ' (' + Utils.patientTargetTypeLabel(target.TargetType!) + ')';
  }

  getBehaviorDataFromBehaviorID(behaviorID: number) {
    const behavior = this.supervisorHelperService.behaviors().find((s) => s.ID === behaviorID);
    if (!behavior) {
      return '';
    }
    return behavior.PatientBehaviorName + ' (' + Utils.patientBehaviorTypeLabel(behavior.BehaviorType!) + ')';
  }

  targetDatasFromSessionID(sessionID: number) {
    const session = this.allSessions.find((s) => s.ID === sessionID);
    if (!session) {
      return [];
    }
    return session.TargetData ?? [];
  }

  behaviorDatasFromSessionID(sessionID: number) {
    const session = this.allSessions.find((s) => s.ID === sessionID);
    if (!session) {
      return [];
    }
    return session.BehaviorData ?? [];
  }
}
